<template>
  <div
    v-if="data.stageLoaded"
    class="i-progress-tracker"
    :class="{ available: !!currentStageData }"
  >
    <template v-if="currentStageData">
      <div
        v-if="isTabletOrLarger"
        class="row"
      >
        <div class="strip"><div class="triangle"></div></div>
        <template
          v-for="stage in careerStageData"
          :key="stage.color"
        >
          <div class="stage">
            <div class="key">
              <div
                class="arrow"
                :class="{ pulse: stage.label === currentStageData.label }"
              >
                <div
                  class="top"
                  :style="{ backgroundColor: stage.index <= currentStageData.index ? stage.color : black200 }"
                ></div>
                <div
                  class="bottom"
                  :style="{ backgroundColor: stage.index <= currentStageData.index ? stage.color : black200 }"
                ></div>
              </div>
              <div class="icon-box">
                <img
                  class="icon"
                  :src="stage.index <= currentStageData.index ? stage.icon : stage.iconGrey"
                />
              </div>
              <div class="label-box">
                <div
                  class="label"
                  :style="{ color: stage.index <= currentStageData.index ? stage.color : black400 }"
                >
                  {{ stage.label }}
                </div>
              </div>
            </div>
            <div
              class="info"
              :class="{ active: stage.label === currentStageData.label }"
              :style="{ color: stage.index <= currentStageData.index ? stage.color : black400 }"
            >
              {{ stage.text }}
            </div>
          </div>
        </template>
      </div>
      <div
        v-else
        class="column"
      >
        <div class="strip"><div class="triangle"></div></div>
        <template
          v-for="stage in careerStageData"
          :key="stage.color"
        >
          <div class="stage">
            <div class="key">
              <div
                class="arrow"
                :class="{ pulse: stage.label === currentStageData.label }"
              >
                <div
                  class="top"
                  :style="{ backgroundColor: stage.index <= currentStageData.index ? stage.color : black200 }"
                ></div>
                <div
                  class="bottom"
                  :style="{ backgroundColor: stage.index <= currentStageData.index ? stage.color : black200 }"
                ></div>
              </div>
              <div class="icon-box">
                <img
                  class="icon"
                  :src="stage.index <= currentStageData.index ? stage.icon : stage.iconGrey"
                />
              </div>
              <div class="label-box">
                <div
                  class="label"
                  :style="{ color: stage.index <= currentStageData.index ? stage.color : black400 }"
                >
                  {{ stage.label }}
                </div>
                <div
                  v-if="!isTabletOrLarger"
                  class="info"
                  :class="{ active: stage.label === currentStageData.label }"
                  :style="{ color: stage.index <= currentStageData.index ? stage.color : black400 }"
                >
                  {{ stage.text }}
                </div>
              </div>
            </div>
            <div
              v-if="isTabletOrLarger"
              class="info"
              :class="{ active: stage.label === currentStageData.label }"
              :style="{ color: stage.index <= currentStageData.index ? stage.color : black400 }"
            >
              {{ stage.text }}
            </div>
          </div>
        </template>
      </div>
    </template>
    <div
      v-else
      class="empty"
    >
      Your progress can be tracked after your first coaching session!
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { useUserStore } from "@/stores/user"
import { black200, black400 } from "@/styles/colors"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import { careerStageData } from "@/helpers/careerStageData"
import api from "@/services/apiClient"

// TYPES
import { MeetingNotesCareerStage } from "@/shared/types/MeetingNotes"

// EMITS
const emit = defineEmits(["update-progress-status"])

// STORES AND COMPOSABLES
const userStore = useUserStore()
const isTabletOrLarger = useIsScreenSize(breakpointMd)

// DATA
const data = reactive({
  careerStage: null as MeetingNotesCareerStage | null,
  stageLoaded: false
})

// COMPUTED
const currentStageData = computed(() => (data.careerStage ? careerStageData[data.careerStage] : null))

// FUNCTIONS
const getCurrentCareerStage = async () => {
  if (userStore.getUserId) {
    try {
      const response = await api.meeting.getListForClient(userStore.getUserId, {
        limit: 1,
        page: 1,
        orderBy: "date",
        direction: "desc",
        status: ["assistedDelivered", "delivered"],
        type: [
          "discovery",
          "coaching",
          "executiveCoaching",
          "recurring",
          "onDemand",
          "scoping",
          "scopingRescheduled",
          "proPackage"
        ]
      })
      data.careerStage = response.meetingNotes?.[0]?.careerStage || null
    } catch {
      console.error(`Error fetching user's latest meeting note. User ID: ${userStore.getUserId}`)
    }
  }
}

// LIFECYCLE HOOKS
onMounted(async () => {
  await getCurrentCareerStage()
  emit("update-progress-status", !!currentStageData.value)
  data.stageLoaded = true
})
</script>

<style lang="scss" scoped>
.i-progress-tracker {
  &.available {
    margin: 0 0 30px 20px;

    @media screen and (min-width: $breakpoint-md) {
      margin: unset;
      padding-left: 20px;
    }
  }

  .empty {
    @include text-lg;

    color: $black400;
  }

  .stage {
    @include flex(column, start, start);

    z-index: 2;

    @media screen and (min-width: $breakpoint-md) {
      @include flex(column, start, stretch, $gap: 22px);
    }

    @media screen and (min-width: $breakpoint-xl) {
      gap: 30px;
    }

    .key {
      @include flex(row, center, center);

      cursor: pointer;

      .arrow {
        @include flex(row, center, center);

        height: 90px;
        width: 80px;
        flex: 0 0 80px;

        @media screen and (min-width: $breakpoint-sm) {
          height: 120px;
          width: 120px;
          flex: 0 0 120px;
        }

        @media screen and (min-width: $breakpoint-md) {
          @include flex(column, start, start);

          width: unset;
          height: unset;
          flex: unset;
        }

        &.pulse {
          animation: pulse 1s forwards ease-in infinite;

          @keyframes pulse {
            0% {
              scale: 1;
              filter: brightness(100%);
            }

            50% {
              scale: 1.05;
              filter: brightness(115%);
            }

            100% {
              scale: 1;
              filter: brightness(100%);
            }
          }
        }

        > div {
          width: 50%;
          height: 50%;

          @media screen and (min-width: $breakpoint-md) {
            width: 35px;
            height: 30px;
          }

          @media screen and (min-width: $breakpoint-xl) {
            width: 55px;
            height: 40px;
          }
        }

        .top {
          transform: skew(0, 40deg);

          @media screen and (min-width: $breakpoint-md) {
            transform: skew(40deg, 0);
          }
        }

        .bottom {
          transform: skew(0, -40deg);

          @media screen and (min-width: $breakpoint-md) {
            transform: skew(-40deg, 0);
          }
        }
      }

      .icon-box {
        @include flex(row, center, center);
        @include border-radius-sm;

        box-shadow: -1px 4px 19.4px 4px #7a7a7a80;
        width: 30px;
        height: 30px;
        flex: 0 0 30px;
        background-color: white;
        z-index: 3;
        transform: translate(-50%, -40%);

        @media screen and (min-width: $breakpoint-sm) {
          width: 35px;
          height: 35px;
          flex: 0 0 35px;
        }

        @media screen and (min-width: $breakpoint-md) {
          transform: unset;
          flex: unset;
          width: 22px;
          height: 22px;
        }

        @media screen and (min-width: $breakpoint-xl) {
          width: 30px;
          height: 30px;
        }

        .icon {
          width: 19px;
          height: 19px;

          @media screen and (min-width: $breakpoint-sm) {
            width: 22px;
            height: 22px;
          }

          @media screen and (min-width: $breakpoint-md) {
            width: 15px;
            height: 15px;
          }

          @media screen and (min-width: $breakpoint-xl) {
            width: 19px;
            height: 19px;
          }
        }
      }

      .label-box {
        @include flex(column, start, start, $gap: 6px);

        transform: translate(-10px, -10px);

        @media screen and (min-width: $breakpoint-md) {
          transform: unset;
        }

        .label {
          @include text-md;

          margin-left: 8px;
          font-weight: 600;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-lg;
          }

          @media screen and (min-width: $breakpoint-md) {
            @include text-sm;

            font-weight: 500;
            margin-left: 5px;
          }

          @media screen and (min-width: $breakpoint-xl) {
            @include text-md;

            margin-left: 8px;
            font-weight: 600;
          }
        }
      }
    }

    .info {
      @include text-xs;

      display: none;
      visibility: hidden;
      width: 100%;
      font-weight: 500;
      padding-left: 8px;

      @media screen and (min-width: $breakpoint-md) {
        @include border-radius-md;

        box-shadow: 0 4px 16px 0 $black100;
        padding: 7px;
        text-align: center;
        display: block;
        width: 95%;
      }

      &.active {
        display: block;
        visibility: visible;
      }
    }

    .key:hover + .info,
    .key:hover > .label-box > .info {
      display: block;
      visibility: visible;
    }

    .key:hover > .label-box {
      margin: 8px 0;

      @media screen and (min-width: $breakpoint-md) {
        margin: unset;
      }
    }
  }

  .row {
    position: relative;

    @media screen and (min-width: $breakpoint-md) {
      @include grid($cols: repeat(5, 1fr), $rows: 1fr, $align-items: stretch, $gap: 0);
    }

    .strip {
      position: absolute;
      background-color: $white;
      box-shadow: 10px 4px 26px 5px $black100;
      z-index: 0;
      height: 38px;
      width: calc(100% + 15px);
      top: 11px;
      left: -10px;

      @media screen and (min-width: $breakpoint-xl) {
        height: 45px;
        width: calc(100% + 25px);
        top: 17.5px;
        left: -15px;
      }

      .triangle {
        width: 0;
        height: 0;
        position: absolute;
        right: -18px;
        border-top: 19px solid transparent;
        border-bottom: 19px solid transparent;
        border-left: 19px solid $white;

        @media screen and (min-width: $breakpoint-xl) {
          right: -21.5px;
          border-top: 22.5px solid transparent;
          border-bottom: 22.5px solid transparent;
          border-left: 22.5px solid $white;
        }
      }
    }
  }

  .column {
    @include flex(column, start, start);

    position: relative;

    .strip {
      position: absolute;
      height: 100%;
      width: 50px;
      left: 15px;
      background-color: $white;
      box-shadow: 0 16px 16px 10px $black100;
      z-index: 0;

      @media screen and (min-width: $breakpoint-sm) {
        box-shadow: 0 22px 18px 10px $black100;
        width: 75px;
        left: 22.5px;
      }

      .triangle {
        width: 0;
        height: 0;
        position: absolute;
        bottom: -24px;
        border-right: 25px solid transparent;
        border-left: 25px solid transparent;
        border-top: 25px solid $white;

        @media screen and (min-width: $breakpoint-sm) {
          bottom: -37.5px;
          border-right: 38.5px solid transparent;
          border-left: 38.5px solid transparent;
          border-top: 38.5px solid $white;
        }
      }
    }
  }
}
</style>
